import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { Select, react } from "uikit";
import { useTranslation } from "react-i18next";

import PaymentSystem, {
	MaibPaymentCurrency,
} from "../../../../../../../../services/PaymentSystem";
import useObjectEditor from "../../../../../../../../hooks/useObjectEditor";
import translationPath from "../../../../constants/translationPath";

import InternalController from "./Controller";

const MaibPaymentsCurrencyBase = react.withController<
	MaibPaymentsCurrency.PropsBase,
	MaibPaymentsCurrency.Controller
>(({ value, onChange, controller }) => {
	const [error, setError] = useState<boolean>(false);

	const { t } = useTranslation();

	const valueEditor = useObjectEditor(value, onChange);

	const maibPaymentsCurrency = valueEditor.useGetter("maibPaymentsCurrency");
	const maibPaymentsCurrencySet = valueEditor.useSetter(
		"maibPaymentsCurrency",
	);

	const selectOptionsWrap = useCallback(
		(item: { id: MaibPaymentCurrency; label: MaibPaymentCurrency }) => ({
			key: item.id,
			value: item.id,
			label: item.label,
		}),
		[],
	);

	const options = useMemo(
		() =>
			Object.values(MaibPaymentCurrency).map((label) =>
				selectOptionsWrap({
					id: label,
					label,
				}),
			),
		[selectOptionsWrap],
	);

	const updateSelectedValues = useCallback(
		(value) => {
			maibPaymentsCurrencySet(value);
			setError(false);
		},
		[maibPaymentsCurrencySet],
	);

	const textError = useMemo(() => {
		if (!error) return "";
		return t([
			`${translationPath}.errors.currency`,
			"Currency not selected",
		]);
	}, [t, error]);

	const placeholder = useMemo(
		() =>
			t([
				`${translationPath}.placeholders.currency`,
				"Choose a currency",
			]),
		[t],
	);

	controller.setContext({ value, setError });
	return (
		<Select
			style={{ width: "200px" }}
			placeholder={placeholder}
			error={textError}
			value={maibPaymentsCurrency}
			options={options}
			onChange={updateSelectedValues}
		/>
	);
}, InternalController);
const MaibPaymentsCurrency = memo(MaibPaymentsCurrencyBase);

declare namespace MaibPaymentsCurrency {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default MaibPaymentsCurrency;
